import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DollarSign, TrendingUp, Calendar, Clock } from "lucide-react";
import { format } from "date-fns";

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
}

interface EmployeeProfile {
  hourly_rate: number | null;
  salary_type: string | null;
  overtime_rate: number | null;
  payment_method: string | null;
}

interface PayrollCalculation {
  id: string;
  period_start: string;
  period_end: string;
  total_hours: number;
  regular_hours: number;
  overtime_hours: number;
  gross_pay: number;
  net_pay: number;
  status: string;
  created_at: string;
}

interface EmployeePayrollViewProps {
  employee: Employee;
  profile: EmployeeProfile | null;
  payrollHistory: PayrollCalculation[];
  onDataUpdate: () => void;
}

export const EmployeePayrollView: React.FC<EmployeePayrollViewProps> = ({
  employee,
  profile,
  payrollHistory
}) => {
  // Calculate totals
  const totalGrossPay = payrollHistory.reduce((sum, payroll) => sum + payroll.gross_pay, 0);
  const totalNetPay = payrollHistory.reduce((sum, payroll) => sum + payroll.net_pay, 0);
  const totalHours = payrollHistory.reduce((sum, payroll) => sum + payroll.total_hours, 0);
  const totalOvertimeHours = payrollHistory.reduce((sum, payroll) => sum + payroll.overtime_hours, 0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Payroll Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <DollarSign className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Gross Pay</p>
                <p className="text-xl font-bold">${totalGrossPay.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Net Pay</p>
                <p className="text-xl font-bold">${totalNetPay.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <Clock className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Hours</p>
                <p className="text-xl font-bold">{totalHours.toFixed(1)}h</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                <Calendar className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Overtime Hours</p>
                <p className="text-xl font-bold">{totalOvertimeHours.toFixed(1)}h</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Current Rates */}
      <Card>
        <CardHeader>
          <CardTitle>Current Pay Rates</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <Label className="text-sm text-muted-foreground">Regular Rate</Label>
              <p className="text-2xl font-bold">${profile?.hourly_rate?.toFixed(2) || '0.00'}/hr</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Overtime Rate</Label>
              <p className="text-2xl font-bold">${profile?.overtime_rate?.toFixed(2) || '0.00'}/hr</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Pay Type</Label>
              <p className="text-lg font-medium capitalize">{profile?.salary_type || 'Not set'}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Payment Method</Label>
              <p className="text-lg font-medium capitalize">
                {profile?.payment_method?.replace('_', ' ') || 'Not set'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payroll History */}
      <Card>
        <CardHeader>
          <CardTitle>Payroll History</CardTitle>
        </CardHeader>
        <CardContent>
          {payrollHistory.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No payroll records found</p>
              <p className="text-sm text-muted-foreground mt-2">
                Payroll records will appear here once processed
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pay Period</TableHead>
                  <TableHead className="text-right">Regular Hours</TableHead>
                  <TableHead className="text-right">Overtime Hours</TableHead>
                  <TableHead className="text-right">Gross Pay</TableHead>
                  <TableHead className="text-right">Net Pay</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Processed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payrollHistory.map((payroll) => (
                  <TableRow key={payroll.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {format(new Date(payroll.period_start), 'MMM dd')} - {format(new Date(payroll.period_end), 'MMM dd, yyyy')}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{payroll.regular_hours.toFixed(1)}h</TableCell>
                    <TableCell className="text-right">{payroll.overtime_hours.toFixed(1)}h</TableCell>
                    <TableCell className="text-right font-medium">
                      ${payroll.gross_pay.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      ${payroll.net_pay.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(payroll.status)} variant="secondary">
                        {payroll.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {format(new Date(payroll.created_at), 'MMM dd, yyyy')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Helper component for consistent labeling
const Label: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <label className={className}>{children}</label>
);